// Utility functions for price formatting and calculations

// Ceil to the next cent, but quantize to 6 decimals first to kill binary float
// noise — otherwise a product like 7.44 × 40 (297.60000000000002 in IEEE-754)
// ceils up to 297.61 and breaks Total == Qty × Unit. (ClickUp 86d3t6v1d)
//
// Mirrored twin: `src/utils/pricing.js`'s `ceilPrice` (the server owner,
// TECH_DEBT #66) — `ceilPriceNegativeZero` in mirroredUtilParity.test.js
// pins the two agree, including at zero.
//
// TECH_DEBT #169 — `Math.ceil(0 * 100 - 1e-6) / 100` is `-0`, not `0`:
// `Math.ceil` of a small negative number that rounds up to zero keeps the
// sign. `result === 0 ? 0 : result` normalises it without touching any other
// value (NaN/null/undefined already returned above, before this line).
export const ceilPrice = (amount: number): number => {
  if (amount === null || amount === undefined || isNaN(amount)) return amount;
  const denoised = Math.round(Number(amount) * 1e6) / 1e6;
  const result = Math.ceil(denoised * 100 - 1e-6) / 100;
  return result === 0 ? 0 : result;
};

/**
 * A number that is finite and greater than zero, or null — never 0, never NaN.
 *
 * F12 (owner decisions 2026-09-25): moved here from
 * `features/quotations/linePriceApplication.ts`, which re-exports it so
 * nothing that already imports it from there breaks. It moved because
 * `lib/vendorInvoiceApi.ts` — the shared HTTP client layer — was importing it
 * FROM that feature folder, a `lib` -> `features` dependency the layering
 * this codebase otherwise keeps (route -> controller -> service -> model on
 * the server; `lib` -> feature-folder UI on the client) does not allow.
 * `utils/` is where a pure, I/O-free helper both `lib` and `features` need
 * belongs — the same reasoning CLAUDE.md states for `src/utils/` on the
 * server. Not a new number-parsing helper (CLAUDE.md's forbidden list, and
 * invariant 8's "never write a new local `num()`"): this is the one existing
 * `positiveNumber` implementation, relocated, not reimplemented — there is
 * still exactly one owner and its behaviour is unchanged.
 */
export const positiveNumber = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The label a money amount is prefixed with on screen:
 * BUSINESS_RULES#quotation-screen-money-labels. "US$", never a bare "$" — the
 * client read "$4.00/KG" as TT$ (2026-09-26) — and "TT$"; any other code as
 * "<CODE> "; no code, no label (a currency is never invented).
 */
export const currencyPrefix = (currency?: string | null): string => {
  const code = String(currency || '').trim().toUpperCase();
  if (code === 'USD') return 'US$';
  if (code === 'TTD') return 'TT$';
  return code ? `${code} ` : '';
};

/**
 * A price as it sits in an EDITABLE field (D104.33): at least 2 decimals
 * ("30.00", "1.50"), more only where the value really has them (0.125), and no
 * thousands separator — the field is parsed back on save. "" when missing.
 */
export const moneyInputText = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n)
    ? ''
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6, useGrouping: false });

/**
 * An amount, ceiled to the cent, grouped, labelled by `currencyPrefix`:
 * "US$1,234.57", "-TT$10.00". (Intl's own en-US currency style prints USD as a
 * bare "$" — the label rule forbids that.)
 *
 * Shape (D104.30): label + thousands separators + exactly 2 decimals, no space;
 * a missing amount is "—". Every screen reads this one formatter.
 *
 * `{ ceil: false }` rounds to the cent instead: for an amount somebody else
 * printed — a supplier's invoice total — which is shown as printed, never
 * ceiled (a printed 10.004 reads US$10.00, not US$10.01).
 */
export const formatCurrency = (
  amount: number | null | undefined,
  currency: string | null = 'USD',
  { ceil = true }: { ceil?: boolean } = {},
): string => {
  // D104.30: a missing amount is "—", never a stated 0.00 (invariant 8).
  if (amount == null || !Number.isFinite(amount)) return "—";
  const shown = ceil ? ceilPrice(amount) : amount;
  const digits = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(shown));
  // The sign of what is SHOWN: under `ceil: false` a float residue like
  // -0.004 rounds to 0.00 and must not read "-US$0.00" (the ceil path already
  // lands on 0 there, via ceilPrice).
  return `${shown < 0 && digits !== '0.00' ? '-' : ''}${currencyPrefix(currency)}${digits}`;
};
